import React from 'react';
import { FiCheckCircle, FiMinus, FiPlus } from 'react-icons/fi';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import {
  lineProduct,
  pickedCount,
  type CleanupLine,
  type LinePick
} from '../quarantineCleanupSteps';

type CleanupLineRowProps = {
  line: CleanupLine;
  pick: LinePick;
  /** The stop's zone has been scanned: the row can be picked. */
  interactive: boolean;
  onPickAll: () => void;
  onSetCount: (count: number) => void;
  onClear: () => void;
};

/**
 * One quarantined (product, lot, expiration) row at the stop. Full pick: "Pick all", or scan the
 * product. Partial pick: the counter (the bulk share) and/or scanning just the serialized units
 * taken — serialized units are never picked by count, so the record names the ones that moved.
 */
function CleanupLineRow({
  line,
  pick,
  interactive,
  onPickAll,
  onSetCount,
  onClear
}: CleanupLineRowProps) {
  const picked = pickedCount(pick);
  const full = picked >= line.quantity;
  const expiration = formatDateOnly(line.expiration) || '—';

  return (
    <div
      className={[
        'cleanup-line',
        full && 'cleanup-line--full',
        picked > 0 && 'cleanup-line--picked'
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="cleanup-line__main">
        <div className="cleanup-line__product">
          <WarehouseProductDisplay product={lineProduct(line)} compact />
          <div className="cleanup-line__lot">
            Lot {line.lot || '—'} · Exp {expiration}
          </div>
        </div>
        <div className="cleanup-line__qty">
          <small>{picked > 0 ? 'Picked' : 'Qty'}</small>
          {picked > 0 ? `${picked}/${line.quantity}` : line.quantity}
        </div>
      </div>

      {interactive && (
        <div className="cleanup-line__actions">
          {line.bulkQuantity > 0 && (
            <div className="cleanup-line__counter" aria-label="Picked by count">
              <button
                type="button"
                aria-label="One less"
                disabled={pick.bulk <= 0}
                onClick={() => onSetCount(pick.bulk - 1)}
              >
                <FiMinus />
              </button>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={line.bulkQuantity}
                aria-label={`Quantity picked of ${line.productName}`}
                value={pick.bulk}
                onChange={(e) => onSetCount(parseInt(e.target.value, 10))}
              />
              <button
                type="button"
                aria-label="One more"
                disabled={pick.bulk >= line.bulkQuantity}
                onClick={() => onSetCount(pick.bulk + 1)}
              >
                <FiPlus />
              </button>
            </div>
          )}
          {line.units.length > 0 && (
            <span className="cleanup-line__units">
              {pick.unitIds.length} of {line.units.length} serialized scanned
            </span>
          )}
          <div className="cleanup-line__buttons">
            {picked > 0 && (
              <button type="button" className="cleanup-line__action" onClick={onClear}>
                Clear
              </button>
            )}
            {full ? (
              <span className="cleanup-line__done">
                <FiCheckCircle aria-hidden /> All picked
              </span>
            ) : (
              <button
                type="button"
                className="cleanup-line__action cleanup-line__action--all"
                onClick={onPickAll}
              >
                Pick all {line.quantity}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CleanupLineRow;
